import { AfterViewChecked, AfterViewInit, Component, ElementRef,
         EventEmitter, Inject, Input, OnChanges, ViewChild, OnDestroy,
         Output, ChangeDetectorRef, SimpleChange
       } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';

import { TemplateConfig, TemplateGroupConfig, TemplatesSettings
       } from 'app/collage-designer/collage-designer-config';
import { DesignStepService
       } from 'app/collage-designer/design-step/design-step.service';
import { PlaceholderType
       } from 'app/collage-designer/stage/placeholder/placeholder';
import { PlaceholderService
       } from 'app/collage-designer/stage/placeholder/placeholder.service';
import { MissingStructureError, PrnglService, RenderRequest
       } from 'app/core/prngl/prngl.service';
import { DesignState } from 'collage-designer/stage/stage-config';
import { StageService } from 'collage-designer/stage/stage.service';
import { GlobalConstants, ToolboxConstants } from 'core/constants';
import { Globals } from 'core/globals';
import { ProductService } from 'core/product/product.service';
import { EventTrackingService } from 'core/tracking/event-tracking.service';
import { Utils } from 'core/utils';
import { AsyncEventEmitterSubscriber } from 'core/utils/async-event-emitter';
import { Subscription } from 'rxjs';
import { GalleryImage, GalleryService } from '../../gallery/gallery.service';

interface TemplateSelection
{
  templateId: string;
  templateGroupId: string;
}

enum PlaceholderFillStrategy {
  GALLERY_IMAGES,
  MOTIVE_STRUCTURE,
}

interface PreviewOverrideConfig {
  part: string;
  strategy: PlaceholderFillStrategy;
  previewType: string;
}

const PREVIEW_OVERRIDE_CONFIGS: Array<PreviewOverrideConfig> =
[
  // NOTE: Not used right now. Enable with #31565.
  // {
  //   part: "motive",
  //   strategy: PlaceholderFillStrategy.GALLERY_IMAGES,
  //   previewType: "pieces",
  // },
  {
    part: "box",
    strategy: PlaceholderFillStrategy.MOTIVE_STRUCTURE,
    previewType: "box",
  },
];

@Component({
    selector: 'template-chooser',
    templateUrl: './template-chooser.component.html',
    styleUrls: ['./template-chooser.component.scss', './modal.scss'],
    standalone: false
})
export class TemplateChooserComponent
    implements AfterViewChecked, AfterViewInit, OnChanges, OnDestroy {
  private static readonly TEMPLATE_GROUPS_PER_PAGE: number = 3;
  private newImageAvaiableSubscription:
      AsyncEventEmitterSubscriber<GalleryImage>;
  private stageConfigAppliedSubscription:
      AsyncEventEmitterSubscriber<boolean>;
  private _templateGroupSelectionIdx: number | undefined = undefined;
  private visitedTemplateGroups: {[id: string]: boolean} = {};
  private subscriptions: Subscription = new Subscription();

  @Input() public templateGroups: Array<TemplateGroupConfig>;
  @Input() public templatesSettings: TemplatesSettings;

  @Input() public selectedTemplateId: string;
  @Output() public selectedTemplateIdChange: EventEmitter<string> =
      new EventEmitter();

  @Input() public selectedTemplateGroupId: string;

  @Output() public selectedTemplateChange: EventEmitter<TemplateSelection> =
      new EventEmitter();
  @Output() public selectedTemplateNotChange: EventEmitter<TemplateSelection> =
      new EventEmitter();

  public groups: Array<TemplateGroupConfig> = [];
  public templateGroupSelection: string | undefined = undefined;
  public loadingUrl: string = Utils.url("./assets/img/loading.gif");

  public trackById: Object = Utils.trackById;
  public itemsPerPage: number;
  public currentPage: number = 1;

  private templatePreviews: {[key: string]: TemplatePreview};
  private previewImageResizeObserver?: ResizeObserver;
  private currentPreviewImageLongSide?: number;

  public constructor(
      private el: ElementRef,
      private mir: MatIconRegistry,
      private sanitizer: DomSanitizer,
      @Inject(GalleryService) private galleryService: GalleryService,
      private changeDetector: ChangeDetectorRef,
      @Inject(PlaceholderService)
          private placeholderService: PlaceholderService,
      @Inject(ProductService) private productService: ProductService,
      @Inject(EventTrackingService) private eventTrackingService:
          EventTrackingService,
      @Inject(DesignStepService) private designStepService: DesignStepService,
      @Inject(PrnglService) private prnglService: PrnglService,
      @Inject(StageService) private stageService: StageService)
  {
    this.newImageAvaiableSubscription = this.galleryService.newImageAvailable
      .subscribe(this.updateSelectedTemplateByImage.bind(this));

    this.stageConfigAppliedSubscription =
        this.stageService.stageConfigApplied.subscribe(async () => {
          this.updateTemplateOverrides();
        });

    this.mir.addSvgIcon(
        'close',
        this.sanitizer.bypassSecurityTrustResourceUrl(Utils.url(
            './assets/img/icons/close.svg')));

    this.templatePreviews = {};

    this.subscriptions.add(
        galleryService.imageStockChanged.subscribe(() => {
          this.updateTemplateOverrides();
        }));

    this.subscriptions.add(
        designStepService.designStepChanged.subscribe(() => {
          this.updateTemplateOverrides();
        }));
  }

  public ngAfterViewChecked(): void {
    this.loadPreviews();
  }

  public async ngOnChanges(
      changes: {[propKey: string]: SimpleChange}): Promise<void>
  {
    if (changes['templateGroups'] && this.templateGroups.length > 0) {
      this.itemsPerPage = this.getItemsPerPage();

      if (this.designStepService.getPart().templateAutoChange &&
          this.galleryService.getImages(true).length > 0) {
            await this.updateSelectedTemplateByImage(
              this.galleryService.getImages(true)[0]);
      }
    }
  }

  private async updateSelectedTemplateByImage(
      image: GalleryImage): Promise<void>
  {
    const alreadyScored = !this.designStepService.getPart().templateAutoChange;
    const isGodMode = Globals.mode === GlobalConstants.MODE_GOD;
    const hasFixedTemplate =
      this.designStepService.getPart().hasPreselectedTemplate;
    const hasChangedTextPlaceholder =
        this.placeholderService.getUnchangedPlaceholders()
          .filter(p => p.type === PlaceholderType.Text).length <
        this.placeholderService.getTextPlaceholders().length;

    if (alreadyScored || isGodMode || hasFixedTemplate ||
        hasChangedTextPlaceholder) {
      return;
    }

    const imageOrientation =
        image.width >= image.height ? 'landscape' : 'portrait';

    const templates = Utils.flattenArray(
      this.templateGroups
        .map((group: TemplateGroupConfig) =>
          Object.keys(group.templates)
            .map((id) => group.templates[id])
            .filter(t => t.imagePlaceholders[imageOrientation] === 1)
            .map(template => ({
              templateGroupId: group.id,
              templateId: template.id,
            }))));

    if (templates.length === 0) {
      return;
    }

    await this.updateSelectedTemplate(
        templates[0].templateGroupId, templates[0].templateId, true);
  }

  public ngOnDestroy(): void
  {
    this.subscriptions.unsubscribe();
    Utils.setBodyScrollable(true);
    this.galleryService.newImageAvailable.unsubscribe(
        this.newImageAvaiableSubscription);
    this.stageService.stageConfigApplied.unsubscribe(
        this.stageConfigAppliedSubscription);
  }

  public getTemplates(templateGroupId?: string): Array<TemplateConfig>
  {
    if (!templateGroupId) {
      templateGroupId = this.selectedTemplateGroupId;
    }

    const _t = Utils.find(
        this.templateGroups, g => g.id === templateGroupId).templates;
    return Object.keys(_t).map(key => _t[key]);
  }

  public ngAfterViewInit(): void {
    this.updateTemplateOverrides();
  }

  public getTemplatePreviews(templateGroupId?: string): Array<TemplatePreview>
  {
    if (!templateGroupId) {
      templateGroupId = this.selectedTemplateGroupId;
    }

    const group = Utils.find(
      this.templateGroups, g => g.id === templateGroupId);
    if (!group) {
      return [];
    }
    const _t = group.templates;
    const templates = Object.keys(_t).map(key => _t[key]);
    let previews = [];

    for (const template of templates) {
      let preview = this.templatePreviews[template.id] || {
        template: template,
        renderRequest: undefined,
        previewUrl: template.previewUrl,
      };

      previews.push(preview);
    }

    return previews;
  }

  public async updateSelectedTemplate(
      templateGroupId: string, templateId: string, autoSelect: boolean = false):
      Promise<void>
  {
    this.placeholderService.resetPendingPlaceholders();

    this.designStepService.getPart().templateAutoChange = false;

    if (this.selectedTemplateGroupId !== templateGroupId ||
        this.selectedTemplateId !== templateId ||
        !autoSelect)
    {
      this.selectedTemplateGroupId = templateGroupId;
      this.selectedTemplateId = templateId;

      this.selectedTemplateChange.emit({
        templateId: templateId,
        templateGroupId: templateGroupId
      });

      this.eventTrackingService.track({
        category: this.productService.getCurrentProduct().id,
        action: 'template_change' + (autoSelect ? '_auto' : ''),
        label: templateId,
      });
    }
    else {
      this.selectedTemplateNotChange.emit();
    }

    this.unsetTemplateGroupSelection();
  }

  public unsetTemplateGroupSelection(): void
  {
    Utils.setBodyScrollable(true);
    this.templateGroupSelection = undefined;
  }

  public async templateGroupClicked(templateGroupId: string): Promise<void>
  {
    Utils.setBodyScrollable(false);
    this.eventTrackingService.track({
      category: this.productService.getCurrentProduct().id,
      action: 'template_group_selected',
      label: templateGroupId,
    });

    const templates = this.getTemplates(templateGroupId);
    if (templates.length === 1) {
      await this.updateSelectedTemplate(templateGroupId, templates[0].id);
    }
    else {
      this.templateGroupSelection = templateGroupId;
      this.updateSelectedTemplateGroupIndex();
    }
  }

  public onPreviewImageError(preview: TemplatePreview): void
  {
    preview.renderRequest = undefined;
    this.updateTemplatePreview(preview);
  }

  public placeholdersCount(template: TemplateConfig): number
  {
    const emptyCount = { portrait: 0, landscape: 0 };
    const count = template.imagePlaceholders || emptyCount;
    return count.portrait + count.landscape;
  }

  public set templateGroupSelectionIdx(idx: number)
  {
    this._templateGroupSelectionIdx = idx;
    this.visitedTemplateGroups[this.templateGroups[idx].id] = true;
  }

  public get templateGroupSelectionIdx(): number | undefined
  {
    return this._templateGroupSelectionIdx;
  }

  public isVisitedTemplateGroup(id: string): boolean
  {
    return !!this.visitedTemplateGroups[id];
  }

  public updateSelectedTemplateGroupIndex(idx?: number): number
  {
    if (idx === undefined) {
      this.templateGroupSelectionIdx =
          this.templateGroups.findIndex(
              (el) => el.id === this.templateGroupSelection);
    }
    else {
      if (idx >= 0 && idx < this.templateGroups.length) {
        this.templateGroupSelectionIdx = idx;
      }
    }

    this.designStepService.getPart().templateAutoChange = false;
    return this.templateGroups.findIndex(
        (el) => el.id === this.templateGroupSelection);
  }

  public pageChanged(page: number): void
  {
    this.currentPage = page;
  }

  public getItemsPerPage(): number
  {
    if (Utils.isSmallDevice() || Utils.isMediumDevice()) {
      return (
          this.templateGroups.length > 1
          ? this.templateGroups.length
          : this.getTemplates(this.templateGroups[0].id).length);
    }
    return TemplateChooserComponent.TEMPLATE_GROUPS_PER_PAGE;
  }

  private loadPreviews(): void
  {
    const previewImages =
        this.el.nativeElement.querySelectorAll('.preview-image');
    if (!previewImages) {
      return;
    }
    previewImages.forEach(e => {
      if (e.src.endsWith('loading.gif') && e.height > 0 && e.width > 0) {
        e.src = e.dataset.src;
      }
    });
  }

  private updateTemplateOverrides(): void
  {
    const designState = this.designStepService.getCurrentDesignState();
    const productId = this.designStepService.getProductId();

    const part = this.designStepService.getDesignablePart(designState);
    if (!part) {
      return;
    }

    const config = PREVIEW_OVERRIDE_CONFIGS.find((c) => c.part === part.id);
    if (!config) {
      return;
    }

    const templates =
      part.templateGroups.flatMap(g => Object.values(g.templates));

    for (const template of templates.filter((t) => !!t.stageConfig)) {
      let req = this.prnglService.makeRenderRequest(
        productId, config.previewType);

      req.fillDesignedParts();

      try {
        switch (config.strategy) {
          case PlaceholderFillStrategy.GALLERY_IMAGES:
            req.addGalleryImagesPart(part.id, template);
            break;

          case PlaceholderFillStrategy.MOTIVE_STRUCTURE:
            req.addMotiveStructurePart(part.id, template);
            break;

          default:
            break;
        }
      }
      catch (e) {
        if (!(e instanceof MissingStructureError)) {
          throw e;
        }

        delete this.templatePreviews[template.id];
        continue;
      }

      this.templatePreviews[template.id] = {
        template: template,
        renderRequest: req,
        previewUrl: template.previewUrl,
      };
    }

    this.updateTemplatePreviews();
  }

  private updateTemplatePreviews(): void
  {
    for (const preview of Object.values(this.templatePreviews)) {
      this.updateTemplatePreview(preview);
    }
    this.changeDetector.detectChanges();
  }

  private updateTemplatePreview(preview: TemplatePreview): void
  {
    let renderRequest = preview.renderRequest;
    if (preview.renderRequest) {
      if (this.currentPreviewImageLongSide) {
        renderRequest.viewportLongSide = this.currentPreviewImageLongSide;
      }
      preview.previewUrl = renderRequest.url;
    } else {
      preview.previewUrl = preview.template.previewUrl;
    }
  }

  @ViewChild('templatePreviewImage') set templatePreviewImageElement(
    el: ElementRef | undefined
  )
  {
    if (!!this.previewImageResizeObserver) {
      this.previewImageResizeObserver.disconnect();
      this.previewImageResizeObserver = undefined;
    }

    if (!!el) {
      const obs = new ResizeObserver((_) => {
        const longSide = Math.max(
          el.nativeElement.offsetWidth,
          el.nativeElement.offsetHeight
        );

        if (longSide > 0) {
          this.previewImageLongside = longSide;
        }
      });

      obs.observe(el.nativeElement);
      this.previewImageResizeObserver = obs;
    }
  }

  private set previewImageLongside(size: number)
  {
    const alignedLongSide = this.prnglService.snapToViewportStep(size);

    if (alignedLongSide === this.currentPreviewImageLongSide) {
      return;
    }

    this.currentPreviewImageLongSide = alignedLongSide;
    this.updateTemplatePreviews();
  }
}

interface TemplatePreview {
  template: TemplateConfig;
  renderRequest?: RenderRequest;
  previewUrl: string;
}
